import { useState } from 'react';
import useLoading from '../useLoading';
import useAlerts from '../useAlerts';
import SalesButtonActionService from '../../services/sale/salesButtonConfigService';
import useApiErrorHandler from '../useApiErrorHandler';
import useConfirmModal from '../useConfirmModal';
import useModals from '../useModals';
import { AppDispatch } from '../../store/store';
import { useDispatch } from 'react-redux';
import { removeCanceledPendingSale, removeSaleFromList, updateSaleAfterForceAnnul, removePendingCashierSaleById, triggerPendingCashierRefresh } from '../../store/slices/sale/saleSlice';
import { useSale } from './useSale';
import { isDteRejectedError, parseDteRejectedObservations } from '../../features/sale/dte/dteRejectedError';

export const useSalesButtonActions = () => {
  const { show: showAlert } = useAlerts();
  const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
  const { handle } = useApiErrorHandler();
  const [loading, setLoading] = useState(false);
  const confirm = useConfirmModal();
  const { openModal } = useModals();
  const dispatch: AppDispatch = useDispatch();

  const { handlerResetSaleWorkspace } = useSale();


  /**
   * 📄 Descargar JSON de la venta
   */
  const handlerDownloadJson = async (saleId: number) => {
    try {
      setLoading(true);
      showLoadingIndicator();
      await SalesButtonActionService.downloadJson(saleId);
    } catch (error: any) {
      handle(error);
    } finally {
      setLoading(false);
      hideLoadingIndicator();
    }
  };

  /**
   * 📧 Enviar por correo
   */
  const handlerSendByEmail = async (saleId: number) => {
    try {
      setLoading(true);
      showLoadingIndicator();
      await SalesButtonActionService.sendByEmail(saleId);
    } catch (error: any) {
      handle(error);
    } finally {
      setLoading(false);
      hideLoadingIndicator();
    }
  };

  /**
 * 🧾 Consultar DTE
 */
  const handlerConsultDTE = async (saleId: number) => {
    try {
      setLoading(true);
      showLoadingIndicator();

      const response = await SalesButtonActionService.consultDTE(saleId);
      const { statusCode, data } = response;

      if (statusCode === 200) {
        if (data?.url) {
          window.open(data.url, '_blank');
        } else {
          showAlert('warning', 'Advertencia', 'No se obtuvo una URL de consulta.');
        }
      }
    } catch (error) {
      showAlert('error', 'Error', 'No se pudo consultar el DTE.');
      handle(error);
    } finally {
      setLoading(false);
      hideLoadingIndicator();
    }
  };

  const handlerForceAnnulRejectedSale = async (saleId: number) => {
    confirm({
      title: '¿Anular venta rechazada?',
      message:
        'Esta venta fue rechazada por el Ministerio de Hacienda.\n\n' +
        'Se realizará una anulación técnica, se revertirá el inventario y se liberará el correlativo.\n\n' +
        '¿Deseas continuar?',
      icon: 'warning',
      confirmText: 'Sí, anular',
      cancelText: 'Cancelar',

      onConfirm: async () => {
        try {
          setLoading(true);
          showLoadingIndicator();

          const response =
            await SalesButtonActionService.forceAnnulRejectedSale(saleId);

          if (response.statusCode === 200 && response.data) {
            const { saleId, status, documentNumber } = response.data;

            // ✅ ACTUALIZACIÓN CORRECTA
            dispatch(
              updateSaleAfterForceAnnul({
                id: saleId,
                status,
                documentNumber,
              })
            );

            openModal(response.type || 'success', {
              title: response.title || 'Anulación realizada',
              message:
                response.message ||
                'La venta fue anulada correctamente.',
            });
          }

        } catch (error) {
          handle(error);
        } finally {
          setLoading(false);
          hideLoadingIndicator();
        }
      },
    });
  };


  const handlerDeleteRejectedSale = async (saleId: number) => {
    confirm({
      title: '¿Eliminar venta rechazada?',
      message:
        'Esta acción eliminará permanentemente la venta del sistema.\n\n' +
        'Solo se puede eliminar si fue rechazada por Hacienda.\n\n' +
        '⚠️ Esta acción no se puede deshacer.\n\n' +
        '¿Deseas continuar?',
      icon: 'warning',
      confirmText: 'Sí, eliminar',
      cancelText: 'Cancelar',

      onConfirm: async () => {
        try {
          setLoading(true);
          showLoadingIndicator();

          const response =
            await SalesButtonActionService.deleteRejectedSale(saleId);

          if (response.statusCode === 200) {

            // 🔥 Remover del store
            dispatch(removeSaleFromList(saleId));

            openModal(response.type || 'success', {
              title: response.title || 'Venta eliminada',
              message:
                response.message ||
                'La venta fue eliminada correctamente.',
            });
          }

        } catch (error) {
          handle(error);
        } finally {
          setLoading(false);
          hideLoadingIndicator();
        }
      },
    });
  };

  /**
 * 🔄 Reenviar DTE
 */
  const handlerResendDTE = async (saleId: number, openPersistedDetail?: (saleId: number) => void) => {
    confirm({
      title: '¿Reenviar DTE?',
      message:
        'Se reenviará el documento electrónico al Ministerio de Hacienda.\n\n' +
        '¿Deseas continuar?',
      icon: 'warning',
      confirmText: 'Sí, reenviar',
      cancelText: 'Cancelar',

      onConfirm: async () => {
        try {
          setLoading(true);
          showLoadingIndicator();

          const response = await SalesButtonActionService.resendDTE(saleId);

          if (response.statusCode === 200) {
            openModal(response.type || 'success', {
              title: response.title || 'Reenvío exitoso',
              message:
                response.message ||
                'El DTE fue reenviado correctamente.',
            });
          }

        } catch (error: any) {
          const envelope = error?.response?.data;
          if (isDteRejectedError(error)) {
            const rawObservations = envelope.data?.observations;
            const observations = parseDteRejectedObservations(rawObservations);
            openModal('error', {
              title: envelope.title || 'DTE rechazado',
              message: `${envelope.message || 'Hacienda rechazó el documento.'}${observations.length ? `\n\nObservaciones:\n${observations.join('\n')}` : ''}`,
            });
            if (openPersistedDetail && window.confirm('¿Desea abrir el detalle persistido de la respuesta de Hacienda?')) {
              openPersistedDetail(saleId);
            }
          } else {
            handle(error);
          }
        } finally {
          setLoading(false);
          hideLoadingIndicator();
        }
      },
    });
  };


  const handlerCancelPendingSale = async (saleId: number) => {
    confirm({
      title: '¿Cancelar venta?',
      message:
        'La venta será cancelada y dejará de aparecer en la lista de ventas pendientes.\n\n' +
        'Esta acción no elimina la información de la venta del sistema.\n\n' +
        '¿Deseas continuar?',
      icon: 'warning',
      confirmText: 'Sí, cancelar',
      cancelText: 'Volver',

      onConfirm: async () => {
        try {
          setLoading(true);
          showLoadingIndicator();

          const response =
            await SalesButtonActionService.cancelPendingSale(saleId);

          if (response.statusCode === 200 && response.data) {
            dispatch(
              removeCanceledPendingSale(response.data.saleId)
            );

            // También sale de la lista de "pendientes de cobro" (módulo de caja)
            dispatch(
              removePendingCashierSaleById(response.data.saleId)
            );
            dispatch(triggerPendingCashierRefresh());

            openModal(response.type || 'success', {
              title: response.title || 'Venta cancelada',
              message:
                response.message ||
                'La venta fue cancelada correctamente.',
            });
            handlerResetSaleWorkspace();
          }

        } catch (error) {
          handle(error);
        } finally {
          setLoading(false);
          hideLoadingIndicator();
        }
      },
    });
  };


  return {
    loading,
    handlerDownloadJson,
    handlerSendByEmail,
    handlerConsultDTE,
    handlerForceAnnulRejectedSale,
    handlerDeleteRejectedSale,
    handlerResendDTE,
    handlerCancelPendingSale
  };
};
